// WebSocket RPC client — single persistent authenticated connection to the API.
// Replaces per-request REST/Bearer: request/response is correlated by id, and the
// server pushes events (session_revoked, balances, txs). Auto-reconnects with
// backoff, except after a session revocation (that means log out).
import { API_BASE, ApiError } from './api';

type Params = Record<string, unknown> | undefined;
type ConnState = 'idle' | 'connecting' | 'authed' | 'closed';

interface AuthArgs {
  initData: string;
  deviceId: string;
  platform?: string;
  label?: string;
  /** Carries an invite code when the app was opened from someone's link. */
  startParam?: string;
}

// Error codes that mean "this session is gone" — surfaced as 401 so the app's
// existing onAuthError logout path handles them.
const REVOKE_CODES = new Set(['session_revoked', 'session_expired', 'invalid_token', 'unauthorized']);
const statusFor = (code: string) => (REVOKE_CODES.has(code) ? 401 : 400);

function deriveWsUrl(base: string): string {
  if (base.startsWith('http')) return base.replace(/^http/, 'ws') + '/ws';
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}
const WS_URL = deriveWsUrl(API_BASE);

let ws: WebSocket | null = null;
let state: ConnState = 'idle';
let authArgs: AuthArgs | null = null;
let lastAuthUser: unknown = null;

/** The public user returned by the last successful auth handshake (seeds useMe so
 *  the app-open reauth gate can decide instantly instead of after a /me fetch). */
export function getAuthUser(): unknown {
  return lastAuthUser;
}
let seq = 0;
let attempts = 0;
let revoked = false; // set on session_revoked → stop reconnecting

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}
const pending = new Map<number, Pending>();
const eventListeners = new Map<string, Set<(d: unknown) => void>>();
const stateListeners = new Set<(s: ConnState) => void>();

function setState(s: ConnState) {
  if (state === s) return;
  state = s;
  stateListeners.forEach((fn) => fn(s));
}

export const connState = () => state;
export const isAuthed = () => state === 'authed';

export function onEvent(ev: string, fn: (d: unknown) => void): () => void {
  let set = eventListeners.get(ev);
  if (!set) {
    set = new Set();
    eventListeners.set(ev, set);
  }
  set.add(fn);
  return () => set!.delete(fn);
}

export function onState(fn: (s: ConnState) => void): () => void {
  stateListeners.add(fn);
  return () => stateListeners.delete(fn);
}

function emit(ev: string, d: unknown) {
  eventListeners.get(ev)?.forEach((fn) => fn(d));
}

function rejectAllPending(err: unknown) {
  for (const [, p] of pending) {
    clearTimeout(p.timer);
    p.reject(err);
  }
  pending.clear();
}

// Low-level send (used by both `auth` handshake and `rpc`). Does NOT wait for auth.
function send(m: string, p: Params, timeoutMs = 20_000): Promise<unknown> {
  return new Promise((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reject(new ApiError(0, 'ws_closed'));
      return;
    }
    const i = ++seq;
    const timer = setTimeout(() => {
      pending.delete(i);
      reject(new ApiError(0, 'timeout'));
    }, timeoutMs);
    pending.set(i, { resolve, reject, timer });
    ws.send(JSON.stringify({ i, m, p }));
  });
}

function open() {
  if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) return;
  setState('connecting');
  ws = new WebSocket(WS_URL);

  ws.onopen = async () => {
    try {
      const res = await send('auth', authArgs as unknown as Params, 15_000);
      // The handshake already returns the public user — stash it so useMe can seed
      // its cache and prompt reauth immediately on open, without a second round-trip.
      lastAuthUser = (res as { user?: unknown } | null)?.user ?? null;
      attempts = 0;
      setState('authed');
    } catch {
      // Auth failed (bad/expired initData) — close; reconnect will retry with
      // fresh initData on the next connect() (app open).
      ws?.close();
    }
  };

  ws.onmessage = (evt) => {
    let msg: {
      i?: unknown;
      r?: unknown;
      e?: { code?: string; status?: number; [k: string]: unknown };
      ev?: unknown;
      d?: unknown;
    };
    try {
      msg = JSON.parse(typeof evt.data === 'string' ? evt.data : String(evt.data));
    } catch {
      return;
    }
    if (typeof msg.i === 'number') {
      const p = pending.get(msg.i);
      if (!p) return;
      pending.delete(msg.i);
      clearTimeout(p.timer);
      if (msg.e) {
        const code = msg.e.code ?? 'internal';
        const status = typeof msg.e.status === 'number' ? msg.e.status : statusFor(code);
        const { code: _c, status: _s, ...data } = msg.e;
        p.reject(new ApiError(status, code, Object.keys(data).length ? data : undefined));
      } else p.resolve(msg.r);
      return;
    }
    if (typeof msg.ev === 'string') {
      if (msg.ev === 'session_revoked') revoked = true;
      emit(msg.ev, msg.d);
    }
  };

  ws.onclose = () => {
    rejectAllPending(new ApiError(0, 'ws_closed'));
    setState('closed');
    if (!revoked && authArgs) scheduleReconnect();
  };
  ws.onerror = () => {
    try {
      ws?.close();
    } catch {
      /* noop */
    }
  };
}

function scheduleReconnect() {
  const delay = Math.min(30_000, 1000 * 2 ** attempts++);
  setTimeout(() => {
    if (!revoked && authArgs) open();
  }, delay);
}

/** Open the connection and authenticate. Resolves once authed. */
export function connect(args: AuthArgs): Promise<void> {
  authArgs = args;
  revoked = false;
  attempts = 0;
  if (state === 'authed') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      off();
      reject(new ApiError(0, 'ws_timeout'));
    }, 20_000);
    const off = onState((s) => {
      if (s === 'authed') {
        clearTimeout(t);
        off();
        resolve();
      }
    });
    open();
  });
}

/** Stop the connection (logout). No reconnect until connect() is called again. */
export function disconnect() {
  revoked = true;
  authArgs = null;
  rejectAllPending(new ApiError(401, 'session_revoked'));
  try {
    ws?.close();
  } catch {
    /* noop */
  }
  ws = null;
  setState('idle');
}

function waitForAuth(timeoutMs = 15_000): Promise<void> {
  if (state === 'authed') return Promise.resolve();
  if (revoked) return Promise.reject(new ApiError(401, 'session_revoked'));
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      off();
      reject(new ApiError(0, 'ws_timeout'));
    }, timeoutMs);
    const off = onState((s) => {
      if (s === 'authed') {
        clearTimeout(t);
        off();
        resolve();
      }
    });
  });
}

// Sponsored-transfer methods do live on-chain work server-side — re-energy
// rental polls for up to ~18s, plus resource/allowance/balance reads — so the
// default 20s RPC deadline races the server and the client gives up ("timeout")
// while the server is still renting. Give these calls a generous deadline.
const LONG_RPC = 90_000;
const LONG_METHODS = new Set([
  'transfers.create',
  'transfers.prepare',
  'transfers.broadcast',
  'cards.issue',
]);

/** Authenticated RPC call. Waits briefly for (re)auth if the socket is mid-connect.
 *  Pass `timeoutMs` to override the deadline for a slow method. */
export async function rpc<T = unknown>(method: string, params?: Params, timeoutMs?: number): Promise<T> {
  if (state !== 'authed') await waitForAuth();
  const deadline = timeoutMs ?? (LONG_METHODS.has(method) ? LONG_RPC : undefined);
  return send(method, params, deadline) as Promise<T>;
}
